import {
  ChevronDoubleDownIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { getPdf } from "../../../../../actions/CarrierAgreementMgmt/VendorInvoice";
import CancelButton from "../../../../../components/CancelButton";
import CloseIconButton from "../../../../../components/CloseIconButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import EditButtonVendorVoice from "../../../../../components/EditButtonVendorVoice";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import ViewOutlineButton from "../../../../../components/ViewOutlineButton";
import { IVendorInvoiceDetails } from "../../InvoiceCapture/types";
import useEditInvoiceReconcilliation from "../hooks/useEditInvoiceReconcilliation";
import ReconciliationAllocationView from "../View/ReconciliationAllocationView";
import ReconcilliationActivityHistory from "../View/ReconcilliationActivityHistory";
import ReconcilliationDetails from "./ReconcilliationDetails";
import InvoiceDebitAdjustmentDetails from "../../InvoiceCapture/view/InvoiceDebitAdjustmentDetails";
import { Disclosure } from "@headlessui/react";

interface IReconcilliationForm {
  onClickCancelShow;
  rowData;
  setShowInvoice;
  activityHistoryData;
}

const ReconcilliationForm: React.FC<IReconcilliationForm> = ({
  onClickCancelShow,
  rowData,
  setShowInvoice,
  activityHistoryData,
}) => {
  const useFormMethods = useForm<IVendorInvoiceDetails>({
    defaultValues: { ...rowData },
  });
  const queryClient = useQueryClient();

  const [isEdit, setIsEdit] = useState(false);
  const [newData, setNewData] = useState<any>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isSaving, SetIsSaving] = useState(false);
  const [isViewPd, setIsViewPdf] = useState(false);

  const {
    formState: { isDirty },
    clearErrors,
    reset,
    handleSubmit,
  } = useFormMethods;

  const {
    mutate: updateInvoiceReconcilliation,
    isSuccess: editVendorInvoiceSuccess,
    data: editVendorInvoiceResult,
    isError: editVendorInvoiceError,
    reset: resetEditVendorInvoiceData,
  } = useEditInvoiceReconcilliation();

  const retrieveFile = async (fileName) => {
    try {
      getPdf(fileName).then((content) => {
        const pdfBlob = new Blob([content.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
      });
    } catch (error) {}
  };
  const onSubmit = (data: IVendorInvoiceDetails) => {
    if (isDirty) {
      setNewData({ ...data, bankReference: data.bankReference?.trim() });
      setIsOpenModal(true);
    } else {
      if (isViewPd) {
        //avoid trigger toast
        setIsViewPdf(false);
      } else {
        toast.fire("Saving Failed", "No changes have been made", "warning");
      }
    }
  };

  const handleSave = () => {
    SetIsSaving(true);
    updateInvoiceReconcilliation(newData);
    setIsOpenModal(false);
  };
  const handleCancel = () => {
    clearErrors();
    reset();
    setIsEdit(false);
    SetIsSaving(false);
  };
  const cancelEdit = () => {
    setIsEdit(false);
    SetIsSaving(false);
  };

  if (editVendorInvoiceSuccess && editVendorInvoiceResult) {
    if (
      editVendorInvoiceResult.status === 201 ||
      editVendorInvoiceResult.status === 200
    ) {
      toast.fire("Success", "Vendor Invoice updated successfully.", "success");
      queryClient.invalidateQueries(["GetPendingReconciliationInvoices"]);
      queryClient.invalidateQueries(["GetInvoiceDetailsById"]);
      handleCancel();
      setShowInvoice(false);
    } else if (editVendorInvoiceResult.status === 500) {
      //has problem with updating the bank reference and bank date
      if (`${editVendorInvoiceResult?.data}`.includes("Duplicate")) {
        toast.fire(
          "Server Error",
          "Failed to Update, Duplicate Vendor Invoice",
          "error"
        );
      } else {
        toast.fire(
          "Server Error",
          "Failed to Update Vendor Invoice No Data Saved",
          "error"
        );
      }
      cancelEdit();
    } else if (editVendorInvoiceResult.status === 400) {
      //sucesfully updated but encountered issue in submit to acumatica
      toast.fire(
        "Submission failed",
        "Vendor Invoice updated successfully but " +
          editVendorInvoiceResult?.data,
        "error"
      );
      queryClient.invalidateQueries(["GetPendingReconciliationInvoices"]);
      queryClient.invalidateQueries(["GetInvoiceDetailsById"]);
      cancelEdit();
    }

    resetEditVendorInvoiceData();
  }
  if (editVendorInvoiceError) {
    toast.fire(
      "Server Error",
      "Failed to Update Vendor Invoice No Data Saved",
      "error"
    );
    queryClient.invalidateQueries(["GetPendingReconciliationInvoices"]);
    queryClient.invalidateQueries(["GetInvoiceDetailsById"]);
    handleCancel();
  }

  return (
    <>
      <div className="box-shadow p-4 border-quinary border-l-2 p-2">
        <div>
          <div className="relative -top-6 -right-4">
            <CloseIconButton
              onClick={() => {
                onClickCancelShow();
              }}
            />
          </div>
        </div>
        <div>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex justify-between items-center border-secondary border-b-2 ">
                <div>
                  <div className="flex gap-2 p-2 items-center">
                    <div>
                      <ReceiptPercentIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                    </div>
                    <div className="flex flex-col leading tight">
                      <h3 className="text-xs font-medium text-gray-500 ">
                        Vendor Invoice Processing
                      </h3>
                      <h1 className="mr-auto font-bold text-primary text-md uppercase pb-2">
                        Batch List - {rowData?.id}
                      </h1>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-xs space-y-2 my-2">
                <div className="ml-auto py-2 flex rounded box-shadow justify-between ">
                  <div className="space-x-4 mr-6 self-center mx-2 text-pink-500 font-bold">
                    {rowData?.vendorName} - {rowData?.vendorCode}
                  </div>
                  <div className="flex items-center  gap-2">
                    <div className=" ">
                      <div className="flex gap-1 mx-2">
                        {isEdit ? (
                          <>
                            <SubmitHookForm
                              type="submit"
                              disabled={false}
                              buttonText={"Save/Submit"}
                              className={undefined}
                              onClick={() => {}}
                            />
                            <CancelButton
                              type="button"
                              onClick={() => {
                                handleCancel();
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <ViewOutlineButton
                              disabled={isSaving}
                              buttonText={"View PDF"}
                              onClick={() => {
                                setIsViewPdf(true);
                                retrieveFile(rowData?.invoiceImageAttachment);
                              }}
                            />
                            <EditButtonVendorVoice
                              disabled={false}
                              onClick={(e) => {
                                setIsEdit(true);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <ReconcilliationDetails isEdit={isEdit} rowData={rowData} />
              </div>
              {/* Debit Adjusment Section */}
              {rowData && rowData?.debitAdjBillRef?.length > 0 && (
                <div className="w-full bg-white">
                  <>
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <div className=" bg-white rounded-lg shadow-lg">
                            <div className="flex flex-row justify-between mt-2">
                              <div className="text-pink-500 font-bold font-sans py-2 px-3">
                                Debit Adjusment
                              </div>
                              <div>
                                <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                  <div className="flex flex-row justify-end">
                                    <div className="flex flex-row mr-4">
                                      <label className="text-quinary font-bold font-sans mr-2">
                                        Bill Adjustment:
                                      </label>
                                      <div className="bg-gray-100 border-gray-100 rounded shadow-md">
                                        <label className="text-black font-bold font-sans ml-2 mr-2">
                                          $
                                          {rowData.billAdjustment.length > 0
                                            ? rowData.billAdjustment.reduce(
                                                (bill, adj) => bill + adj,
                                                0
                                              )
                                            : rowData.billAdjustment[0]}
                                        </label>
                                      </div>
                                    </div>
                                    <ChevronDoubleDownIcon
                                      className={`${
                                        open ? "transform rotate-180" : ""
                                      }  text-gray-500 w-5 h-5 mr-4`}
                                    />
                                  </div>
                                </Disclosure.Button>
                              </div>
                            </div>
                            <Disclosure.Panel className="text-xxs">
                              <InvoiceDebitAdjustmentDetails
                                rowData={rowData?.debitAdjustmentDetails}
                              />
                            </Disclosure.Panel>
                            <div className="flex flex-row justify-end py-2 px-3 mr-7">
                              <div className="bg-gray-100 border-4 border-gray-100 rounded shadow-md">
                                <label className="text-quinary font-bold font-sans ml-2 mr-2">
                                  Total Amount Paid:
                                </label>
                                <label className="text-black font-bold font-sans mr-2">
                                  {rowData.billAdjustment.length > 0
                                    ? "$" +
                                      (rowData.amountPaid -
                                        rowData.billAdjustment.reduce(
                                          (bill, adj) => bill + adj,
                                          0
                                        ))
                                    : "$"}
                                </label>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </Disclosure>
                  </>
                </div>
              )}
              <div className="w-full">
                <ReconciliationAllocationView
                  allocationViewData={
                    rowData && rowData.itemizeDetails?.length > 0
                      ? rowData.itemizeDetails
                      : []
                  }
                />

                <ReconcilliationActivityHistory
                  activityHistoryData={activityHistoryData}
                />
              </div>
            </form>
          </FormProvider>
        </div>
        {isOpenModal && (
          <ConfirmBoxModal
            ModalTitle={`Reconcilliation`}
            qConfirmation={`Are you sure you want to update this Invoice ?`}
            isOpen={isOpenModal}
            onClose={() => {
              setIsOpenModal(false);
            }}
            onClickFunction={() => {
              handleSave();
            }}
            cancelButtonRef={undefined}
          />
        )}
      </div>
    </>
  );
};

export default ReconcilliationForm;
